import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs/operators';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../../components/form';
import Button from '../../../../components/form/elements/button';
import Checkbox from '../../../../components/form/elements/checkbox';
import DateElement from '../../../../components/form/elements/date';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Readonly from '../../../../components/form/elements/readonly';
import Select, { Option } from '../../../../components/form/elements/select';
import Decimal from '../../../../components/form/elements/decimal';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import Watch from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import { CropWorkType } from '../../../../enums/crop-work-types';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Field from '../../../../interfaces/field';
import Paginate from '../../../../interfaces/paginate';
import { CROP_FIELDS_AND_WORKS_INDEX } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route, when } from '../../../../services/utils';
import { FormData } from './create';
import FieldSelect from './field-select';
import { Section } from '../../../../pages/crop/fields-and-works';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import ClassifierValueProperty from '../../../../interfaces/classifierValueProperty';
import { useParams } from 'react-router-dom';
import Disabled from '../../../../components/form/elements/disabled';
import Switch from '../../../../components/form/elements/switch';
import Work from '../../../../interfaces/work';

const schema = yup.object().shape({
  work: yup.object().shape({
    value: yup.string().required().uuid(),
  }),
  name: when(
    (value, parent) => parent.work?.label === 'Cits',
    yup.string().required(),
    yup.string().nullable()
  ),
  date: yup.date().required(),
  fields: yup.lazy(() =>
    yup
      .array()
      .of(
        yup.object().shape({
          field: yup.object().required().shape({
            value: yup.string().uuid(),
          }),
          treated_area: yup
            .number()
            .positive()
            .decimal()
            .moreThan(0, 'Field must be a positive number'),
        })
      )
      .min(1)
  ),
});

export interface WorkSubmitData {
  work: Option & { work: ClassifierValue };
  name: string;
  date: Date;
  created_at: Date;
  done: boolean;
  fields: {
    field: Option & { field: Field };
    lad_number: string;
    area: number;
    treated_area: number;
    crop_value: string;
    delete_field_crop?: boolean;
    plant_development_stage?: Option;
    works: Work[];
  }[];
}

export default function StepWork({
  formData,
  onSubmit,
}: {
  formData: FormData;
  onSubmit: SubmitType<WorkSubmitData>;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );

  const { classifierService } = useService();
  const { farmId, season } = useParams<{
    farmId: string;
    season: string;
  }>();

  const [body] = useState(() => [
    Group(
      [
        Readonly([Text('type_name', tp('type'))]),
        Watch(
          Select('work', {
            asyncOptions: (value, loadedOptions, { page }) => {
              return classifierService
                .valuesPaged(ClassifierGroup.CROP, Classifier.CROP_WORKS, {
                  page,
                  value,
                  crop_work_type: formData.type_id,
                })
                .pipe(
                  map((response: Paginate<ClassifierValue>) => ({
                    hasMore: response.last_page > response.current_page,
                    options: response.data.map((work) => ({
                      label: work.value,
                      value: work.id,
                      work: work,
                    })),
                    additional: { page: response.current_page + 1 },
                  }))
                );
            },
            label: tp('work'),
          }),
          ({ value, getValues, setValue }) => {
            const work = value?.work as ClassifierValue;
            if (value?.work?.properties) {
              const deleteFieldCrop = Object.values(work.properties).find(
                (property) =>
                  property?.property?.key ===
                  ClassifierPropertyKey.DELETE_FIELD_CROP
              )?.value;

              let iLast = 50;
              for (let i = 0; i <= iLast; i++) {
                if (
                  getValues(
                    'fields.'.concat(i.toString(), '.delete_field_crop')
                  ) !== undefined &&
                  getValues('fields.'.concat(i.toString(), '.crop_value'))
                ) {
                  setValue(
                    'fields.'.concat(i.toString(), '.delete_field_crop'),
                    deleteFieldCrop === true
                  );
                  iLast = i + (iLast > 500 ? 0 : iLast);
                }
              }
            }
          },
          false
        ),
        Show(
          Text('name', tp('name')),
          'work',
          (work: Option) => work?.label === 'Cits'
        ),
        DateElement('date', tp('date')),
        Switch(
          Readonly(Checkbox('done', tp('done'))),
          Checkbox('done', tp('done')),
          'done',
          () => formData.done === true
        ),
      ],
      tp('work')
    ),
    FieldSelect(
      () => [
        Disabled(Decimal('treated_area', tp('treated_area')), {
          watchOn: '_fields__1__field',
          callback: (field) => (field?.value ? false : true),
        }),
        Readonly([
          Text(
            'lad_number',
            t('pages.crop.fields_and_works.fields.lad_number')
          ),
          Decimal('area', t('pages.crop.fields_and_works.fields.area')),
          Text('crop_value', t('pages.crop.fields_and_works.fields.crop')),
          Show(
            Checkbox('delete_field_crop', tp('delete_field_crop')),
            '',
            () => {
              if (
                formData.type_id === CropWorkType.SOIL_TREATMENT ||
                formData.type_id === CropWorkType.THRESHING ||
                formData.type_id === CropWorkType.VEGETABLE_ETC_HARVESTING
              ) {
                return true;
              }
              return false;
            }
          ),
        ]),
        Show(
          Select('plant_development_stage', {
            asyncOptions: (
              value,
              loadedOptions,
              { page, parentName, getValues }
            ) => {
              const params: any = {
                page,
                value: value,
              };

              const field = getValues(parentName.concat('.field'));
              const fieldCropId = field?.field?.crop_id;

              if (fieldCropId) {
                params.plant_development_stage_category = fieldCropId;
              }

              return classifierService
                .valuesPaged(
                  ClassifierGroup.CROP,
                  Classifier.PLANT_DEVELOPMENT_STAGES,
                  params
                )
                .pipe(
                  map((response) => {
                    return {
                      hasMore: response.last_page > response.current_page,
                      options: response.data.map((plant_development_stage) => {
                        const description = Object.values(
                          plant_development_stage.properties
                        ).find(
                          (property: ClassifierValueProperty) =>
                            property.property.key ===
                            ClassifierPropertyKey.PLANT_DEVELOPMENT_STAGE_DESCRIPTION
                        ).value;

                        return {
                          label: plant_development_stage.value.concat(
                            ' - ',
                            description
                          ),
                          value: plant_development_stage.id,
                        };
                      }),
                      additional: {
                        page: response.current_page + 1,
                      },
                    };
                  })
                );
            },
            multi: false,
            label: tp('plant_development_stage'),
            cacheOn: ['field.value'],
          }),
          'field',
          (value: Option & { field: Field }) => {
            return (
              value?.field?.crop_id != null &&
              formData.type_id === CropWorkType.DISPERSION_OR_SPRAYING
            );
          }
        ),
      ],
      'fields',
      tp('fields')
    ),
  ]);

  const [footer] = useState(() => [
    Link(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      }),
      'cancel'
    ),
    Button('continue', null, tp('continue'), faLongArrowRight),
  ]);

  return (
    <div>
      <Form
        title={formData.id ? tp('edit') : tp('create')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema}
      />
    </div>
  );
}
